// Unpublished Work © 2023-2024 Deere & Company.
import { Grid, IconButton, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  DataGridPro,
  gridClasses,
  GridColDef,
  GridColumnHeaderParams,
  gridFilteredTopLevelRowCountSelector,
  GridRenderCellParams,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";
import InvoiceDetailsActionButtons from "./InvoiceDetailsActionButtons";
import InvoiceLineItemsNoResultsOverlay from "./InvoiceLineItemsNoResultsOverlay";
import InvoiceLineItemsNoRowsOverlay from "./InvoiceLineItemsNoRowsOverlay";
import InvoiceLineItemsPrintView from "./InvoiceLineItemsPrintView";
import {
  HandlingUnit,
  InvoiceLineItem,
  TimeLimit,
} from "../../../../api/invoices/interfaces";
import { BusinessAction } from "../../../../api/rbac/types";
import DataGridProSearch from "../../../../components/DataGridProSearch/DataGridProSearch";
import { surchargeRenderToggleOn } from "../../../../components/DetailedInvoiceSummary/surchargeConditionHelper";
import { ConfigContext } from "../../../../context/ConfigContext";
import { DealerAccountContext } from "../../../../context/DealerAccountContext";
import { RBACAccessContext } from "../../../../context/RBACAccessContext/RBACAccessContext";
import { UserContext } from "../../../../context/UserContext/UserContext";
import {
  CANADA_DEALER_LOCATION,
  CHINA_DEALER_LOCATION,
  US_DEALER_LOCATION,
} from "../../../../utils/constants";
import ExpandGroup from "../../AllInvoices/components/ExpandGroup";
import InvoicesPagination from "../../AllInvoices/components/InvoicesPagination";
import { columnsHeaderSx, dataGridSx } from "../../AllInvoices/style";

interface InvoiceLineItemsProps {
  isOrderInvoice: boolean;
  sendRef?: any;
  countryCurrency: string | undefined;
  reversed?: boolean;
  invoiceLineItems: InvoiceLineItem[] | undefined;
  error: boolean;
  reload: () => void;
  loading: boolean;
  timeLimit?: TimeLimit;
  isVendorDirect?: boolean;
  onDownloadToExcelClick?: () => void;
}

interface DataGridRow {
  id: string;
  lineNumber?: number;
  documentNumber?: string;
  partNumber?: string;
  description?: string;
  quantity?: number;
  unitPrice?: number;
  extendedDiscount?: number;
  freightCost?: number;
  extendedAmount?: number;
  deliveryNumber?: string;
  handlingUnits?: HandlingUnit[];
  handlingUnit: HandlingUnit;
  group: any[];
}

const PAGE_SIZE = 10;

export const conversion = (
  value: number | undefined,
  lang: string | undefined,
  countryCurrency: string | undefined
) =>
  value?.toLocaleString(lang?.replace("_", "-"), {
    style: "currency",
    currency: countryCurrency || "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }) ?? "";

const InvoiceLineItems = ({
  isOrderInvoice,
  sendRef,
  countryCurrency,
  reversed = false,
  invoiceLineItems,
  error,
  reload,
  loading,
  timeLimit,
  isVendorDirect,
  onDownloadToExcelClick,
}: InvoiceLineItemsProps) => {
  const { invoiceNumber } = useParams();
  const [page, setPage] = useState<number>(1);
  const [visibleRowsCount, setVisibleRowsCount] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const theme = useTheme();
  const [printButtonClicked, setPrintButtonClicked] = useState<boolean>(false);
  const [closedNestedRows, toggleClosedNestedRows] = useState<boolean>(false);
  const { t, i18n } = useTranslation("invoices");
  const { dealerAccount, dealerLocation } = useContext(DealerAccountContext);
  const accountId = dealerAccount?.id as string;
  const isChinaDealer = dealerLocation === CHINA_DEALER_LOCATION;
  const {
    ENABLE_HU_FOR_INVOICES,
    ENABLE_INVOICE_DETAILS_PORTLAND_SURCHARGE,
    ENABLE_CORE_PRICE,
  } = useContext(ConfigContext);
  const user = useContext(UserContext);
  const { isRBACEnabled } = useContext(RBACAccessContext);
  const isUSDealer = dealerLocation === US_DEALER_LOCATION;
  const isCADealer = dealerLocation === CANADA_DEALER_LOCATION;

  const PORTLAND_SURCHARGE_TOGGLE_ON_RENDER = surchargeRenderToggleOn(
    isUSDealer,
    isCADealer,
    ENABLE_INVOICE_DETAILS_PORTLAND_SURCHARGE
  );

  const shouldShowHULinks =
    (!isRBACEnabled ||
      (isRBACEnabled &&
        user?.isAuthorizedTo(BusinessAction.VIEW_HANDLING_UNITS))) &&
    ENABLE_HU_FOR_INVOICES;

  useEffect(() => {
    if (invoiceLineItems) {
      setVisibleRowsCount(invoiceLineItems.length);
    }
  }, [invoiceLineItems]);

  useEffect(() => {
    if (closedNestedRows) {
      toggleClosedNestedRows(false);
    }
  }, [closedNestedRows]);

  const columns: GridColDef[] = [];

  const createColumnDef = (
    field: string,
    headerName: string,
    sortable = true,
    testId?: string
  ): GridColDef => ({
    field,
    headerName,
    sortable,
    disableColumnMenu: true,
    minWidth: 160,
    renderHeader: (params: GridColumnHeaderParams) => (
      <>
        <Typography sx={columnsHeaderSx} data-testid={testId || field}>
          {params.colDef.headerName}
        </Typography>
      </>
    ),
    renderCell: (params: GridRenderCellParams) => (
      <Grid data-testid={`${params.row.id}-${testId || field}`}>
        {params.value}
      </Grid>
    ),
    headerAlign: "left",
    align: "left",
    type: "string",
    filterable: true,
    flex: 1,
  });

  const rows = invoiceLineItems ? createRowData(invoiceLineItems) : [];

  const renderCurrencyCell = (
    colName: string,
    params: GridRenderCellParams,
    corePrice: number = 0
  ) => (
    <Grid>
      <Grid data-testid={`${params.row.id}-${colName}`}>
        {conversion(params.row[colName], i18n.language, countryCurrency)}
      </Grid>
      {ENABLE_CORE_PRICE && corePrice > 0 && (
        <Typography
          data-fuel-size="xs"
          data-testid={`${params.row.id}-core-${colName}`}
        >
          Core: {conversion(corePrice, i18n.language, countryCurrency)}
        </Typography>
      )}
    </Grid>
  );

  const filterNumbers = (filterValue: string, data: number) =>
    data?.toString().toLowerCase().includes(filterValue.toLowerCase()) ||
    conversion(data, i18n.language, countryCurrency)
      .toLowerCase()
      .includes(filterValue.toLowerCase());

  columns.push({
    ...createColumnDef("lineNumber", t("invoiceLineItems.header.lineNumber")),
    renderCell: (params: GridRenderCellParams) => (
      <Grid data-testid={`${params.row.id}-lineNumber`}>
        <ExpandGroup {...params} closedNestedRows={closedNestedRows} />
        {params.row.lineNumber}
      </Grid>
    ),
    renderHeader: () => (
      <>
        <IconButton disabled />
        <Typography sx={columnsHeaderSx} data-testid="lineNumber">
          {t("invoiceLineItems.header.lineNumber")}
        </Typography>
      </>
    ),
  });
  columns.push(
    createColumnDef(
      "documentNumber",
      t("invoiceLineItems.header.documentNumber")
    )
  );
  columns.push(
    createColumnDef(
      "dealerReference",
      t("invoiceLineItems.header.dealerReference")
    )
  );
  if (isOrderInvoice) {
    columns.push({
      ...createColumnDef(
        "handlingUnit",
        t("invoiceLineItems.header.handlingUnit")
      ),
      renderCell: (params: GridRenderCellParams) =>
        params.row.handlingUnit && (
          <div data-testid={`${params.row.id}-handlingUnit`}>
            <Typography color="primary" data-fuel-size="sm">
              {shouldShowHULinks ? (
                <Link
                  key={params.id}
                  to={`/dealer/${accountId}/handlingUnits/${params.row.handlingUnit.number}`}
                  data-testid={`${params.row.handlingUnit.number}-handlingUnit-details-link`}
                >
                  <Typography
                    sx={{
                      color: theme.tokens.FuelColorTextBrandPrimary,
                      fontWeight: theme.tokens.FuelFontWeightRegular,
                      fontSize: "14px",
                      textDecoration: "underLine",
                      ":hover": {
                        cursor: "pointer",
                        color: theme.tokens.FuelColorFocused,
                      },
                    }}
                  >
                    {params.row.handlingUnit.number}
                  </Typography>
                </Link>
              ) : (
                params.row.handlingUnit.number
              )}
            </Typography>
            <Typography color="tertiary" data-fuel-size="sm">
              ({params.row.handlingUnit.quantity}{" "}
              {params.row.handlingUnit.quantityUnit})
            </Typography>
          </div>
        ),
      valueGetter: (value: HandlingUnit) => value && value.number,
      getApplyQuickFilterFn:
        (value: string) =>
        (_, _row): boolean => {
          const parentDocument: DataGridRow | undefined = rows.find(
            (row: DataGridRow) => row.lineNumber === _row.group[0]
          );
          if (!parentDocument) {
            return false;
          }

          const doesSiblingMatch = parentDocument.handlingUnits!.find((shu) =>
            shu.number.includes(value)
          );

          const parentDocumentIncludesMatch = [
            "documentNumber",
            "partNumber",
            "deliveryNumber",
            "description",
            "quantity",
            "unitPrice",
            "extendedDiscount",
            "freightCost",
            "extendedAmount",
          ].map((rowData) =>
            // @ts-ignore
            parentDocument[rowData]
              ?.toString()
              .toLowerCase()
              .includes(value.toLowerCase())
          );

          return (
            parentDocumentIncludesMatch.includes(true) || !!doesSiblingMatch
          );
        },
    });
  }
  if (isChinaDealer && isOrderInvoice) {
    columns.push(
      createColumnDef(
        "deliveryNumber",
        t("invoiceLineItems.header.deliveryNumber")
      )
    );
  }
  columns.push(
    createColumnDef("partNumber", t("invoiceLineItems.header.partNumber"))
  );
  columns.push(
    createColumnDef("description", t("invoiceLineItems.header.description"))
  );
  columns.push(
    createColumnDef("quantity", t("invoiceLineItems.header.quantity"))
  );
  columns.push({
    ...createColumnDef("unitPrice", t("invoiceLineItems.header.unitPrice")),
    renderCell: (params: GridRenderCellParams) =>
      renderCurrencyCell("unitPrice", params, params.row.coreUnitPrice),
    getApplyQuickFilterFn:
      (value: string) =>
      (_, row): boolean =>
        filterNumbers(value, row.unitPrice),
  });
  columns.push({
    ...createColumnDef(
      "extendedDiscount",
      t("invoiceLineItems.header.extendedDiscount")
    ),
    renderCell: (params: GridRenderCellParams) =>
      renderCurrencyCell("extendedDiscount", params),
    getApplyQuickFilterFn:
      (value: string) =>
      (_, row): boolean =>
        filterNumbers(value, row.extendedDiscount),
  });
  if (PORTLAND_SURCHARGE_TOGGLE_ON_RENDER) {
    columns.push({
      ...createColumnDef(
        "flashPlusCharges",
        t("invoiceLineItems.header.surcharges"),
        true,
        "surcharges"
      ),
      renderCell: (params: GridRenderCellParams) =>
        renderCurrencyCell("flashPlusCharges", params),
      getApplyQuickFilterFn:
        (value: string) =>
        (_, row): boolean =>
          filterNumbers(value, row.flashPlusCharges),
    });
    columns.push({
      ...createColumnDef(
        "freightCharges",
        t("invoiceLineItems.header.freightCost"),
        true,
        "freightCost"
      ),
      renderCell: (params: GridRenderCellParams) =>
        renderCurrencyCell("freightCharges", params),
      getApplyQuickFilterFn:
        (value: string) =>
        (_, row): boolean =>
          filterNumbers(value, row.freightCharges),
    });
  } else {
    columns.push({
      ...createColumnDef(
        "freightCharges",
        t("invoiceLineItems.header.surcharges"),
        true,
        "surcharges"
      ),
      renderCell: (params: GridRenderCellParams) =>
        renderCurrencyCell("freightCharges", params),
      getApplyQuickFilterFn:
        (value: string) =>
        (_, row): boolean =>
          filterNumbers(value, row.freightCharges),
    });
  }

  columns.push({
    ...createColumnDef(
      "extendedAmount",
      t("invoiceLineItems.header.extendedAmount")
    ),
    renderCell: (params: GridRenderCellParams) =>
      renderCurrencyCell("extendedAmount", params, params.row.corePrice),
    getApplyQuickFilterFn:
      (value: string) =>
      (_, row): boolean =>
        filterNumbers(value, row.extendedAmount),
  });

  const apiRef = useGridApiRef();

  const updateSearchTerm = (newSearchTerm: string) => {
    apiRef.current.setQuickFilterValues(
      newSearchTerm.split(" ").filter((word) => word !== "")
    );
    setSearchTerm(newSearchTerm);
    setPage(1);
    toggleClosedNestedRows(true);
  };

  return (
    <Grid data-testid="invoice-line-items-table">
      <Grid
        padding="20px 0"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography
          variant="h3"
          sx={{ color: theme.tokens.FuelColorTextPrimary }}
          data-testid="invoice-line-items-header"
        >
          {invoiceNumber} ({invoiceLineItems?.length ?? 0})
        </Typography>
        {!printButtonClicked && (
          <DataGridProSearch
            sx={{ width: "25%" }}
            searchTerm={searchTerm}
            updateSearchTerm={updateSearchTerm}
          />
        )}
      </Grid>
      {!printButtonClicked ? (
        <DataGridPro
          apiRef={apiRef}
          columnBufferPx={columns.length + 1}
          columns={columns}
          rows={rows}
          hideFooter
          autoHeight
          treeData
          getTreeDataPath={(row) => row.group}
          disableColumnResize
          onStateChange={(state) => {
            const parentRowCount = gridFilteredTopLevelRowCountSelector(
              state,
              apiRef.current.instanceId
            );

            setVisibleRowsCount(parentRowCount);
          }}
          pagination
          paginationModel={{ page: page - 1, pageSize: PAGE_SIZE }}
          disableChildrenSorting
          initialState={{
            pagination: {
              paginationModel: { page: page - 1, pageSize: PAGE_SIZE },
            },
            columns: {
              columnVisibilityModel: { __tree_data_group__: false },
            },
          }}
          slots={{
            noRowsOverlay: () => (
              <InvoiceLineItemsNoRowsOverlay
                error={error}
                loading={loading}
                reload={reload}
              />
            ),
            noResultsOverlay: () => (
              <InvoiceLineItemsNoResultsOverlay
                searchTerm={searchTerm}
                updateSearchTerm={updateSearchTerm}
              />
            ),
          }}
          sx={{
            ...dataGridSx(
              theme,
              gridClasses,
              !!visibleRowsCount,
              printButtonClicked
            ),
            [`.${gridClasses.cell}`]: {
              display: "flex",
              alignItems: "center",
              borderColor: theme.tokens.FuelColorBorderLight,
              lineHeight: theme.tokens.FuelLineHeightSm,
            },
          }}
        />
      ) : (
        <InvoiceLineItemsPrintView
          isCorePriceEnabled={ENABLE_CORE_PRICE}
          isOrderInvoice={isOrderInvoice}
          isChinaDealer={isChinaDealer}
          rows={rows}
          countryCurrency={countryCurrency}
        />
      )}
      {!printButtonClicked ? (
        <Grid
          display="flex"
          justifyContent="flex-end"
          padding="0 48px"
          bottom="0"
          margin="auto -3.25rem -1.563rem"
          zIndex="5"
          sx={{
            backgroundColor: theme.tokens.FuelColorBackgroundWhite,
          }}
        >
          <InvoicesPagination
            page={page}
            setPage={setPage}
            pageSize={PAGE_SIZE}
            rowLength={visibleRowsCount}
          />
          <InvoiceDetailsActionButtons
            isOrderInvoice={isOrderInvoice}
            sendRef={sendRef}
            setPrintButtonClicked={setPrintButtonClicked}
            reversed={reversed}
            timeLimit={timeLimit}
            isVendorDirect={isVendorDirect}
            onDownloadToExcelClick={onDownloadToExcelClick}
          />
        </Grid>
      ) : (
        <></>
      )}
    </Grid>
  );
};

const parentData = (lineItem: InvoiceLineItem) => ({
  id: `${lineItem.lineNumber}`,
  ...lineItem,
  handlingUnit: lineItem.handlingUnits[0],
  group: [lineItem.lineNumber],
});

const childData = (lineItem: InvoiceLineItem) =>
  lineItem.handlingUnits.slice(1).map((huLineItem, index) => ({
    id: `${lineItem.lineNumber}.${index}`,
    handlingUnit: huLineItem,
    group: [lineItem.lineNumber, huLineItem.number],
  }));

const createRowData = (lineItems: InvoiceLineItem[]) =>
  lineItems.flatMap((lineItem: InvoiceLineItem) => [
    parentData(lineItem),
    ...childData(lineItem),
  ]);

export default InvoiceLineItems;
